import axios from "axios";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Sparkles,
  Crown,
  Trophy,
  Star,
  CheckCircle2,
  AlertTriangle,
  Printer,
  Loader2,
} from "lucide-react";
import { API } from "@/context/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const WEEKDAY_PT_LONG = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
];

const RANK_BG = [
  "bg-amber-400",
  "bg-slate-300",
  "bg-orange-300",
  "bg-slate-200",
  "bg-slate-200",
];

function formatCreatedAt(iso) {
  try {
    const d = new Date(iso);
    return `${WEEKDAY_PT_LONG[d.getDay()]}, ${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} às ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch {
    return iso;
  }
}

function initials(name) {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

function scoreClass(score) {
  const s = Number(score) || 0;
  if (s >= 8) return "bg-emerald-100 text-emerald-800 border-emerald-300";
  if (s >= 6) return "bg-blue-100 text-blue-800 border-blue-300";
  if (s >= 4) return "bg-amber-100 text-amber-800 border-amber-300";
  return "bg-rose-100 text-rose-800 border-rose-300";
}

const ComparisonPrintPage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useDocumentTitle(
    data?.job_title
      ? `Comparação — ${data.job_title}`
      : "Comparação de candidatos",
  );

  useEffect(() => {
    if (!id) return;
    axios
      .get(`${API}/comparisons/${id}`)
      .then((res) => setData(res.data))
      .catch((err) =>
        setError(
          err?.response?.data?.detail ||
            "Não foi possível carregar a comparação.",
        ),
      )
      .finally(() => setLoading(false));
  }, [id]);

  // Auto-trigger Print dialog once the data has rendered (gives the
  // browser a moment to layout images/fonts before the dialog opens).
  useEffect(() => {
    if (!data) return;
    const t = setTimeout(() => {
      try {
        window.print();
      } catch {
        /* user can still click the button */
      }
    }, 600);
    return () => clearTimeout(t);
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-sm text-slate-600">
          {error || "Comparação não encontrada."}
        </div>
      </div>
    );
  }

  const candidates = data.candidates || [];
  const comp = data.comparison || {};
  const winner = comp.final?.winner_candidate_id;
  const ranking = comp.final?.ranking || [];
  const candidatesById = candidates.reduce((acc, c) => {
    acc[c.id] = c;
    return acc;
  }, {});

  return (
    <div className="print-root bg-white text-slate-900 mx-auto" data-testid="comparison-print-root">
      {/* Print-specific styles */}
      <style>{`
        @page { size: A4 landscape; margin: 14mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          aside, nav, header[role="banner"] { display: none !important; }
          main { margin: 0 !important; padding: 0 !important; max-width: 100% !important; }
          .page-break { break-after: page; }
        }
        @media screen {
          .print-root { max-width: 1100px; padding: 32px; }
        }
        .pill { border-radius: 9999px; padding: 2px 10px; font-size: 11px; font-weight: 600; }
      `}</style>

      {/* Floating print button (hidden when actually printing) */}
      <button
        type="button"
        onClick={() => window.print()}
        className="no-print fixed top-4 right-4 z-50 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold shadow-lg"
        data-testid="print-button"
      >
        <Printer className="w-4 h-4" />
        Imprimir / Salvar PDF
      </button>

      {/* Header */}
      <header className="mb-6 pb-4 border-b-2 border-slate-200 flex items-start justify-between gap-6">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-violet-600 mb-1">
            GridRecruiter · Análise comparativa
          </div>
          <h1 className="text-2xl font-bold text-slate-900 leading-tight">
            {data.job?.title || "Comparação de candidatos"}
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            {data.job?.department && `${data.job.department} · `}
            {data.job?.location || ""}
          </p>
        </div>
        <div className="text-right text-[11px] text-slate-500 shrink-0">
          <div className="font-semibold">Comparação gerada em</div>
          <div>{formatCreatedAt(data.created_at)}</div>
          <div className="mt-1">{candidates.length} candidatos analisados</div>
        </div>
      </header>

      {/* Final recommendation banner */}
      {winner && candidatesById[winner] && (
        <section
          className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-4 mb-6"
          data-testid="print-final"
        >
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-400 text-white flex items-center justify-center shrink-0">
              <Crown className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-700">
                Recomendação final da IA
              </div>
              <div className="text-lg font-bold text-slate-900">
                {candidatesById[winner]?.name}
              </div>
              <p className="text-sm text-slate-700 mt-1.5 leading-relaxed">
                {comp.final?.justification || "Sem justificativa."}
              </p>
              {ranking.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mt-3">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    Ranking:
                  </span>
                  {ranking.map((rid, i) => {
                    const c = candidatesById[rid];
                    if (!c) return null;
                    return (
                      <span
                        key={rid}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border ${
                          i === 0
                            ? "bg-amber-50 border-amber-300 text-amber-800"
                            : "bg-white border-slate-200 text-slate-700"
                        }`}
                      >
                        <span className="font-bold tabular-nums">#{i + 1}</span>
                        {c.name}
                        {i === 0 && <Star className="w-3 h-3 fill-current" />}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Candidate cards */}
      <section className="grid gap-3 mb-6" style={{ gridTemplateColumns: `repeat(${candidates.length}, minmax(0, 1fr))` }}>
        {candidates.map((c, i) => {
          const isWinner = c.id === winner;
          const rank = ranking.indexOf(c.id) + 1 || i + 1;
          const pc = (comp.per_candidate || []).find((p) => p.candidate_id === c.id);
          return (
            <article
              key={c.id}
              className={`rounded-lg p-3 border ${
                isWinner
                  ? "border-emerald-400 bg-emerald-50/50"
                  : "border-slate-200 bg-white"
              }`}
            >
              <div className="flex items-start gap-2 mb-2">
                <div className={`w-9 h-9 rounded-md text-white flex items-center justify-center text-sm font-bold shrink-0 ${RANK_BG[Math.min(rank - 1, 4)]}`}>
                  {initials(c.name)}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold tabular-nums text-slate-500">
                      #{rank}
                    </span>
                    <div className="text-sm font-bold text-slate-900 leading-tight">
                      {c.name}
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Score IA: <strong className="tabular-nums">{c.score}</strong>/100
                    {c.experience_years
                      ? ` · ${c.experience_years} anos`
                      : ""}
                  </div>
                </div>
              </div>
              {pc?.verdict && (
                <p className="text-[11px] text-slate-700 italic leading-snug mb-2 border-l-2 border-slate-300 pl-2">
                  {pc.verdict}
                </p>
              )}
              {pc?.relative_strengths?.length > 0 && (
                <div className="mb-1.5">
                  <div className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 flex items-center gap-1">
                    <CheckCircle2 className="w-2.5 h-2.5" />
                    Destaques
                  </div>
                  <ul className="text-[11px] text-slate-700 mt-0.5 space-y-0.5">
                    {pc.relative_strengths.map((s, k) => (
                      <li key={k} className="pl-2 relative before:content-['•'] before:absolute before:left-0 before:text-emerald-500">
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {pc?.relative_concerns?.length > 0 && (
                <div>
                  <div className="text-[9px] uppercase tracking-wider font-bold text-amber-700 flex items-center gap-1">
                    <AlertTriangle className="w-2.5 h-2.5" />
                    Atenção
                  </div>
                  <ul className="text-[11px] text-slate-700 mt-0.5 space-y-0.5">
                    {pc.relative_concerns.map((s, k) => (
                      <li key={k} className="pl-2 relative before:content-['•'] before:absolute before:left-0 before:text-amber-500">
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </article>
          );
        })}
      </section>

      {/* Criteria matrix */}
      {comp.criteria?.length > 0 && (
        <section className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-1.5">
            <Trophy className="w-3.5 h-3.5 text-amber-600" />
            <h2 className="text-sm font-bold text-slate-900">Matriz comparativa por critério</h2>
            <span className="text-[10px] text-slate-500 ml-1">(notas 0-10)</span>
          </div>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="bg-slate-100/60">
                <th className="text-left px-3 py-1.5 font-bold text-slate-700 w-1/4">
                  Critério
                </th>
                {candidates.map((c) => (
                  <th
                    key={c.id}
                    className="text-left px-2 py-1.5 font-bold text-slate-700 border-l border-slate-200"
                  >
                    {c.name?.split(/\s+/)[0] || "—"}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comp.criteria.map((row, ri) => (
                <tr key={ri} className={ri % 2 ? "bg-slate-50/40" : ""}>
                  <td className="px-3 py-1.5 font-semibold text-slate-800 align-top">
                    {row.name}
                  </td>
                  {candidates.map((c) => {
                    const sc = row.scores?.find((s) => s.candidate_id === c.id);
                    return (
                      <td
                        key={c.id}
                        className="px-2 py-1.5 align-top border-l border-slate-200"
                      >
                        <div className={`inline-block pill border ${scoreClass(sc?.score)}`}>
                          {sc?.score ?? 0}
                          <span className="opacity-60">/10</span>
                        </div>
                        {sc?.comment && (
                          <div className="text-[10px] text-slate-600 mt-1 leading-snug">
                            {sc.comment}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Footer */}
      <footer className="mt-6 pt-3 border-t border-slate-200 text-[10px] text-slate-400 flex justify-between">
        <span>
          <Sparkles className="w-2.5 h-2.5 inline -mt-0.5 mr-1" />
          Análise gerada por Claude Sonnet 4.5 · GridRecruiter
        </span>
        <span>Documento confidencial — uso interno apenas</span>
      </footer>
    </div>
  );
};

export default ComparisonPrintPage;
